import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { MessageBoardItem, MESSAGE_BOARD_ITEM_TYPE } from 'src/app/models/utils/message-board.model';
import { CoreService } from '../../core/core/core.service';
import { WatcherService } from '../watcher/watcher.service';
import { VIEW_MESSAGE_BOARD_ITEM } from '../watcher/watcher-event';

import findIndex from 'lodash/findIndex';
import each from 'lodash/each';

@Injectable({
  providedIn: 'root',
})
export class MessageBoardService {
  /** The collection of available message board items */
  private _messageBoardItems: MessageBoardItem[] = [];

  /** Event emitter when there are changes in message board items */
  public messageBoardUpdate: Subject<void> = new Subject();

  /** Event emitter to show/hide the message board component */
  public showHideMessageBoard: Subject<boolean> = new Subject();

  /** Event emitter to update the number of print retries */
  public printRetryChanged: Subject<boolean> = new Subject();

  /** Event emitter to toggle the display state of the message board component */
  public toggleMessageBoard: Subject<boolean> = new Subject();

  /** The count of orders retrying to print */
  private _order_retrying: number = 0;

  constructor(public core: CoreService, public watcher: WatcherService) {}

  /**
   * Adds a message board item to the list
   * @param message The message board item to add
   * @param overwrite Flagger to overwrite the existing message board item. Items are distinct by ID.
   */
  addItem(message: MessageBoardItem, overwrite: boolean = false) {
    const index: number = findIndex(this._messageBoardItems, (item: MessageBoardItem) => item.ID == message.ID);
    if (index == -1) {
      message.updateDateTime(this.core.Server_time);
      this._messageBoardItems.unshift(message);
      this.updateBoard();
    } else if (overwrite) {
      this._messageBoardItems.splice(index, 1);

      message.updateDateTime(this.core.Server_time);
      this._messageBoardItems.unshift(message);
      this.updateBoard();
    }
  }

  /**
   * Remove message board items by ID
   * @param id The message board item ID to remove
   */
  removeItem(id: number) {
    const index: number = findIndex(this._messageBoardItems, (item: MessageBoardItem) => item.ID == id);
    if (index > -1) {
      this._messageBoardItems.splice(index, 1);
      this.updateBoard();
    }
  }

  /**
   * Fires a VIEW_MESSAGE_BOARD_ITEM event and passes the item as a parameter.
   * @param item The MessageBoardItem to pass.
   * @param role Optional. The role to attached to event.
   */
  viewItem(item: MessageBoardItem, role?: string) {
    if (item) {
      this.watcher.broadcast(VIEW_MESSAGE_BOARD_ITEM, { item, role });
    }
  }

  getItemById(id: number): MessageBoardItem | null {
    const index: number = findIndex(this._messageBoardItems, (item: MessageBoardItem) => item.ID == id);
    if (index > -1) {
      return this._messageBoardItems[index];
    }

    return null;
  }

  /**
   * Fires a view item on all PRINTER_ERROR to retry printing
   */
  retryAllFailedPrintingError() {
    const ids: number[] = [];
    each(this._messageBoardItems, (item: MessageBoardItem) => {
      if (item && item.Type == MESSAGE_BOARD_ITEM_TYPE.PRINTER_ERROR) {
        ids.push(item.ID);
      }
    });

    // grab the IDS to collect rather than removing it on the fly and loop
    each(ids, (id: number) => {
      const item: MessageBoardItem = this.getItemById(id);
      if (item) {
        this.viewItem(item, 'retry');
      }
    });
  }

  /**
   * A lookup to find the first message board item using a key value pair in the item DETAILS property.
   * @param key The object key
   * @param value The object value
   * @param type Optional. The type of message board item to find.
   * @returns A MessageBoardItem or null
   */
  findItemByDetailsKeyValuePair(
    key: string,
    value: any,
    type?: MESSAGE_BOARD_ITEM_TYPE | string,
  ): MessageBoardItem | null {
    let retval: MessageBoardItem;
    each(this._messageBoardItems, (item: MessageBoardItem) => {
      try {
        if (item.Details[key] == value) {
          if (!type || type == item.Type) {
            retval = item;
            return false;
          }
        }
      } catch (e) {
        return;
      }
    });

    return retval;
  }

  /**
   * A lookup to find ALL the message board item using a key value pair in the item DETAILS property.
   * @param key The object key
   * @param value The object value
   * @param type Optional. The type of message board item to find.
   * @returns A MessageBoardItem or null
   */
  findAllItemsByDetailsKeyValuePair(
    key: string,
    value: any,
    type?: MESSAGE_BOARD_ITEM_TYPE | string,
  ): MessageBoardItem[] {
    let retval: MessageBoardItem[] = [];

    each(this._messageBoardItems, (item: MessageBoardItem) => {
      try {
        if (item.Details[key] == value) {
          if (!type || type == item.Type) {
            retval.push(item);
          }
        }
      } catch (e) {
        return;
      }
    });

    return retval;
  }

  /**
   * Removes all message board items from the list
   */
  clear() {
    this._messageBoardItems.splice(0);
    this.updateBoard();
  }

  /** Emit the message board update */
  updateBoard() {
    this.messageBoardUpdate.next();
  }

  /** Emits an event to show the message board component */
  show() {
    this.showHideMessageBoard.next(true);
  }

  /** Emits an event to hide the message board component */
  hide() {
    this.showHideMessageBoard.next(false);
  }

  /** Emits an event to toggle the display of message board component */
  toggle() {
    this.toggleMessageBoard.next();
  }

  retryingOrder() {
    this._order_retrying++;
    this.printRetryChanged.next();
  }

  retryOrderComplete() {
    this._order_retrying = Math.max(0, this._order_retrying - 1);
    this.printRetryChanged.next();
  }

  /** Reset the status of the message board */
  reset() {
    this._order_retrying = 0;
    this.clear();
  }

  public get Retry_order_count(): number {
    return this._order_retrying;
  }

  public get Message_board_items(): MessageBoardItem[] {
    return this._messageBoardItems || [];
  }
}
